/**
 * QueryService - ビューモデル生成・クエリ操作を管理
 */

import type { 
  AppState, 
  SetViewModel, 
  GraphViewModel,
  Image
} from '../types'

export class QueryService {
  /**
   * SetViewModelを生成
   */
  static getSetViewModel(state: AppState, setId: string): SetViewModel | null {
    const set = state.imageSets[setId]
    if (!set) return null
    
    const items = Object.values(state.setItems)
      .filter(item => item.setId === setId)
      .sort((a, b) => a.order - b.order)
    
    const images = items.map(item => state.images[item.imageId]).filter(Boolean)
    const nodes = Object.values(state.nodes).filter(n => n.setId === setId)
    
    const nodeIds = new Set(nodes.map(n => n.id))
    const connections = {
      incoming: Object.values(state.connections).filter(c => nodeIds.has(c.toNodeId)),
      outgoing: Object.values(state.connections).filter(c => nodeIds.has(c.fromNodeId))
    }
    
    return {
      set,
      images: images.filter((img): img is Image => img !== undefined),
      items,
      nodes,
      connections
    }
  }

  /**
   * GraphViewModelを生成
   */
  static getGraphViewModel(state: AppState): GraphViewModel {
    const adjacencyList = new Map<string, string[]>()
    
    // 隣接リストを構築
    Object.values(state.connections).forEach(conn => {
      if (!adjacencyList.has(conn.fromNodeId)) {
        adjacencyList.set(conn.fromNodeId, [])
      }
      const fromList = adjacencyList.get(conn.fromNodeId)
      if (fromList) {
        fromList.push(conn.toNodeId)
      }
      
      if (conn.bidirectional) {
        if (!adjacencyList.has(conn.toNodeId)) {
          adjacencyList.set(conn.toNodeId, [])
        }
        const toList = adjacencyList.get(conn.toNodeId)
        if (toList) {
          toList.push(conn.fromNodeId)
        }
      }
    })
    
    return {
      sets: Object.values(state.imageSets),
      nodes: Object.values(state.nodes),
      connections: Object.values(state.connections),
      adjacencyList
    }
  }

  /**
   * ストレージ使用状況を取得
   */
  static getStorageInfo(state: AppState, storageUsed: number) {
    return {
      imageCount: Object.keys(state.images).length,
      setCount: Object.keys(state.imageSets).length,
      itemCount: Object.keys(state.setItems).length,
      nodeCount: Object.keys(state.nodes).length,
      connectionCount: Object.keys(state.connections).length,
      storageUsed
    }
  }
}